import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaTrash, FaTractor, FaHome, FaTree, FaCheck } from 'react-icons/fa';

const Services = () => {
  const services = [
    {
      id: 1,
      icon: <FaTrash />,
      title: 'Demolition',
      description: 'Complete demolition services for residential, commercial and industrial structures. We safely remove all types of buildings and structures.',
      features: [
        'Residential demolition',
        'Commercial demolition',
        'Interior demolition',
        'Structural demolition',
        'Debris removal'
      ],
      link: '/services'
    },
    {
      id: 2,
      icon: <FaTractor />,
      title: 'Site Clearing',
      description: 'Professional site clearing and land preparation for new construction projects. We prepare your property for development.',
      features: [
        'Land clearing',
        'Brush removal',
        'Grading and leveling',
        'Excavation services',
        'Site preparation'
      ],
      link: '/services'
    },
    {
      id: 3,
      icon: <FaHome />,
      title: 'Concrete Removal',
      description: 'Expert concrete removal services for driveways, patios, foundations, and more. We make way for your new installations.',
      features: [
        'Driveway removal',
        'Patio demolition',
        'Foundation removal',
        'Sidewalk removal',
        'Concrete recycling'
      ],
      link: '/services'
    },
    {
      id: 4,
      icon: <FaTree />,
      title: 'Tree & Stump Removal',
      description: 'Complete tree and stump removal services for site preparation or property maintenance. We clear obstacles for your project.',
      features: [
        'Tree removal',
        'Stump grinding',
        'Root removal',
        'Land clearing',
        'Debris cleanup'
      ],
      link: '/services'
    }
  ];

  return (
    <ServicesSection className="section">
      <div className="container">
        <div className="section-title animate-on-scroll slide-down">
          <h2>Our Services</h2>
          <p>Professional site clearing and demolition services to prepare your property for development</p>
        </div>
        
        <ServicesGrid>
          {services.map((service, index) => (
            <ServiceCard 
              key={service.id} 
              className={`animate-on-scroll ${index % 2 === 0 ? 'slide-up' : 'slide-down'} delay-${(index + 1) * 100}`}
            >
              {service.image && (
                <ServiceImage>
                  <img src={service.image} alt={service.title} />
                </ServiceImage>
              )}
              <ServiceContent>
                <ServiceIcon>{service.icon}</ServiceIcon>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
                <ServiceFeatures>
                  {service.features.map((feature, idx) => (
                    <ServiceFeature key={idx}>
                      <FaCheck /> {feature}
                    </ServiceFeature>
                  ))}
                </ServiceFeatures>
              </ServiceContent>
              <ServiceLinkWrapper>
                <ServiceLink to={service.link}>Learn More</ServiceLink>
              </ServiceLinkWrapper>
            </ServiceCard>
          ))}
        </ServicesGrid>
        
        <CtaContainer className="animate-on-scroll slide-up delay-300">
          <h3>Ready to start your next project?</h3>
          <p>Contact us today for a free consultation and estimate</p>
          <Link to="/contact#quote-form" className="btn">Get Free Quote</Link>
        </CtaContainer>
      </div>
    </ServicesSection>
  );
};

const ServicesSection = styled.section`
  background-color: var(--light-gray);
  padding: 8rem 0;
  
  .section-title {
    text-align: center;
    margin-bottom: 6rem;
    
    h2 {
      margin-bottom: 1.5rem;
    }
    
    p {
      max-width: 600px;
      margin: 0 auto;
      font-size: 1.8rem;
      color: var(--dark-gray);
    }
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3rem;
  margin: 5rem 0;
  
  @media (min-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceContent = styled.div`
  padding: 4rem 3rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const ServiceIcon = styled.div`
  font-size: 4.5rem;
  color: var(--primary-color);
  margin-bottom: 2rem;
  
  svg {
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  }
`;

const ServiceTitle = styled.h3`
  font-size: 2.4rem;
  margin-bottom: 1.5rem;
  color: #333333;
`;

const ServiceDescription = styled.p`
  font-size: 1.6rem;
  color: #555555;
  margin-bottom: 2.5rem;
  flex-grow: 0;
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  flex-grow: 1;
`;

const ServiceFeature = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 1.2rem;
  font-size: 1.5rem;
  color: #444444;
  
  svg {
    color: var(--primary-color);
    margin-right: 1rem;
    font-size: 1.4rem;
    flex-shrink: 0;
  }
`;

const ServiceLinkWrapper = styled.div`
  padding: 2rem 3rem;
  border-top: 1px solid #f0f0f0;
  background-color: #f9f9f9;
`;

const ServiceLink = styled(Link)`
  font-weight: 600;
  color: var(--primary-color);
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 1.4rem;
  display: inline-flex;
  align-items: center;
  position: relative;
  transition: color 0.3s ease;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: var(--primary-color);
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    color: var(--secondary-color);
    
    &:after {
      transform: scaleX(1);
      transform-origin: left;
    }
  }
`;

const CtaContainer = styled.div`
  text-align: center;
  background-color: var(--primary-color);
  color: white;
  padding: 5rem;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-top: 6rem;
  
  h3 {
    color: white;
    font-size: 3.2rem;
    margin-bottom: 1.5rem;
  }
  
  p {
    font-size: 1.8rem;
    max-width: 600px;
    margin: 0 auto 3rem;
    opacity: 0.9;
  }
  
  .btn {
    background-color: var(--secondary-color);
    color: white;
    padding: 1.5rem 3.5rem;
    font-size: 1.6rem;
    font-weight: 700;
    border-radius: 5px;
    text-transform: uppercase;
    letter-spacing: 1px;
    
    &:hover {
      background-color: white;
      color: var(--primary-color);
    }
  }
`;

const ServiceImage = styled.div`
  width: 100%;
  height: 250px;
  overflow: hidden;
  border-radius: 10px 10px 0 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

export default Services; 